import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Reveal } from "./Reveal";
import { CONFIG_OPTIONS, IMAGES, CONTACT } from "../../lib/content";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Enquiry() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    configuration: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please share your name, email and phone.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      setDone(true);
      toast.success("Thank you — our team will reach out shortly.");
      setForm({ name: "", email: "", phone: "", configuration: "", message: "" });
    } catch (err) {
      toast.error(
        err?.response?.data?.detail
          ? "Please check your details and try again."
          : "Something went wrong. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="enquire"
      data-testid="enquire"
      className="relative bg-[#070707] py-28 md:py-40 overflow-hidden scroll-mt-20"
    >
      {/* faint backdrop */}
      <div className="absolute inset-0 opacity-[0.08]">
        <img
          src={IMAGES.forest}
          alt=""
          aria-hidden
          className="w-full h-full object-cover"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-[#070707] via-transparent to-[#070707]" />

      <div className="relative mx-auto max-w-[1400px] px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-14 lg:gap-24 items-center">
          <div>
            <Reveal>
              <span className="text-xs tracking-luxe uppercase text-[#d4af37]">
                Private Enquiry
              </span>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-serif-lux text-4xl md:text-6xl mt-6 leading-[1.05] text-[#f5f5f0]">
                Begin your ascent to{" "}
                <span className="italic gold-gradient-text">Sarrgosa.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 text-[#9a968c] leading-relaxed max-w-md">
                Share a few details and a Sarrgosa relationship manager will
                send you the private brochure, floor plans and an invitation to
                a curated site visit.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="mt-10 space-y-3">
                {[
                  "Confidential, no-obligation consultation",
                  "Curated helicopter & road site visits",
                  "Priority access to launch inventory",
                ].map((t) => (
                  <div key={t} className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full border border-[#d4af37]/40 flex items-center justify-center">
                      <Check size={12} className="text-[#d4af37]" />
                    </span>
                    <span className="text-sm text-[#c9c5ba]">{t}</span>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.15}>
            <div className="gold-border glass p-8 md:p-10">
              {done ? (
                <motion.div
                  data-testid="enquiry-success"
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="py-14 text-center"
                >
                  <span className="mx-auto mb-6 w-16 h-16 rounded-full border border-[#d4af37]/50 flex items-center justify-center">
                    <Check size={26} className="text-[#d4af37]" />
                  </span>
                  <h3 className="font-serif-lux text-3xl text-[#f5f5f0]">
                    Your request is received
                  </h3>
                  <p className="mt-3 text-[#9a968c]">
                    We'll be in touch within one business day.
                  </p>
                  <div className="mt-6 flex flex-col items-center gap-2 text-sm">
                    <a
                      href={CONTACT.phoneHref}
                      className="text-[#d4af37] hover:underline"
                    >
                      {CONTACT.phone}
                    </a>
                    <a
                      href={CONTACT.emailHref}
                      className="text-[#d4af37] hover:underline break-all"
                    >
                      {CONTACT.email}
                    </a>
                  </div>
                  <button
                    data-testid="enquiry-reset"
                    onClick={() => setDone(false)}
                    className="btn-ghost rounded-full px-7 py-3 mt-8 text-xs tracking-[0.16em] uppercase"
                  >
                    Send another enquiry
                  </button>
                </motion.div>
              ) : (
                <form onSubmit={submit} className="space-y-5" data-testid="enquiry-form">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field label="Full Name">
                      <Input
                        data-testid="enquiry-name"
                        value={form.name}
                        onChange={set("name")}
                        placeholder="Your name"
                        className="bg-black/30 border-[#d4af37]/20 text-[#f5f5f0] placeholder:text-[#6b6b64] focus-visible:ring-[#d4af37] h-12"
                      />
                    </Field>
                    <Field label="Phone">
                      <Input
                        data-testid="enquiry-phone"
                        value={form.phone}
                        onChange={set("phone")}
                        placeholder="+91"
                        className="bg-black/30 border-[#d4af37]/20 text-[#f5f5f0] placeholder:text-[#6b6b64] focus-visible:ring-[#d4af37] h-12"
                      />
                    </Field>
                  </div>

                  <Field label="Email">
                    <Input
                      data-testid="enquiry-email"
                      type="email"
                      value={form.email}
                      onChange={set("email")}
                      placeholder="you@email.com"
                      className="bg-black/30 border-[#d4af37]/20 text-[#f5f5f0] placeholder:text-[#6b6b64] focus-visible:ring-[#d4af37] h-12"
                    />
                  </Field>

                  <Field label="Preferred Residence">
                    <Select
                      value={form.configuration}
                      onValueChange={set("configuration")}
                    >
                      <SelectTrigger
                        data-testid="enquiry-config"
                        className="bg-black/30 border-[#d4af37]/20 text-[#f5f5f0] focus:ring-[#d4af37] h-12"
                      >
                        <SelectValue placeholder="Select a configuration" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0f0f0f] border-[#d4af37]/20 text-[#f5f5f0]">
                        {CONFIG_OPTIONS.map((o) => (
                          <SelectItem key={o} value={o} className="focus:bg-[#d4af37]/15">
                            {o}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>

                  <Field label="Message (optional)">
                    <Textarea
                      data-testid="enquiry-message"
                      value={form.message}
                      onChange={set("message")}
                      placeholder="Tell us what you're looking for..."
                      rows={3}
                      className="bg-black/30 border-[#d4af37]/20 text-[#f5f5f0] placeholder:text-[#6b6b64] focus-visible:ring-[#d4af37] resize-none"
                    />
                  </Field>

                  <button
                    data-testid="enquiry-submit"
                    type="submit"
                    disabled={loading}
                    className="btn-gold w-full rounded-full py-4 text-xs tracking-[0.18em] uppercase font-semibold disabled:opacity-60"
                  >
                    {loading ? "Sending..." : "Request the Private Brochure"}
                  </button>
                  <p className="text-[11px] text-[#6b6b64] text-center leading-relaxed">
                    By submitting, you agree to be contacted by Diamond India
                    Builders regarding Sarrgosa, Kasauli.
                  </p>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[11px] tracking-[0.16em] uppercase text-[#9a968c] mb-2 block">
        {label}
      </span>
      {children}
    </label>
  );
}
